import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { observer } from "mobx-react-lite";
import {
  Button,
  ListBox,
  SearchField,
  Select,
  Spinner,
  Table,
  Tooltip,
} from "@heroui/react";
import { ROUTES } from "@/constants/routes";
import { pushCampaignsStore } from "@/stores/PushCampaignsStore";
import {
  CAMPAIGN_STATUS_LABELS,
  CAMPAIGN_STATUS_FILTER_OPTIONS,
  CAMPAIGN_STATUS_FILTER_ALL,
} from "@/stores/push-campaigns/pushCampaignLabels";
import "./PushCampaignsPage.css";

const COLUMN_COUNT = 8;

function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const local = d.toLocaleString("ru-RU", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
  const msk = d.toLocaleString("ru-RU", {
    timeZone: "Europe/Moscow",
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
  return `${local} (МСК ${msk})`;
}

function pct(part: number, total: number): string {
  if (!total) return "";
  return `${Math.round((part / total) * 100)}%`;
}

const PushCampaignsPage = observer(() => {
  const navigate = useNavigate();
  const store = pushCampaignsStore;

  useEffect(() => {
    void store.loadCampaigns();
  }, [store]);

  const sentAt = (c: {
    scheduledAt?: string | null;
    sentAt?: string | null;
    status: string;
  }) =>
    c.status === "scheduled" || c.status === "draft"
      ? c.scheduledAt
      : c.sentAt ?? c.scheduledAt;

  const statusSelectKey = store.listStatus || CAMPAIGN_STATUS_FILTER_ALL;

  return (
    <div className="pc-page" data-testid="push-campaigns-page">
      <div className="pc-page__toolbar" data-testid="push-campaigns-toolbar">
        <h1 className="text-xl font-semibold">Push-рассылки</h1>
        <Button
          variant="primary"
          data-testid="push-campaigns-create-button"
          onPress={() => navigate(ROUTES.PUSH_CAMPAIGN_NEW)}
        >
          Создать
        </Button>
      </div>

      {store.listError && (
        <div className="pc-error-banner" role="alert" data-testid="push-campaigns-error">
          {store.listError}
        </div>
      )}

      <div className="pc-page__filters" data-testid="push-campaigns-filters">
        <SearchField
          className="min-w-[240px]"
          data-testid="push-campaigns-search"
          value={store.listSearch}
          onChange={(v) => store.setListSearch(v)}
          aria-label="Поиск по названию"
        >
          <SearchField.Group>
            <SearchField.SearchIcon />
            <SearchField.Input placeholder="Поиск по названию" />
            <SearchField.ClearButton />
          </SearchField.Group>
        </SearchField>
        <Select
          className="min-w-[180px]"
          data-testid="push-campaigns-status-filter"
          selectedKey={statusSelectKey}
          onSelectionChange={(key) =>
            store.setListStatus(String(key ?? CAMPAIGN_STATUS_FILTER_ALL))
          }
          aria-label="Статус"
        >
          <Select.Trigger>
            <Select.Value />
            <Select.Indicator />
          </Select.Trigger>
          <Select.Popover>
            <ListBox>
              {CAMPAIGN_STATUS_FILTER_OPTIONS.map((opt) => (
                <ListBox.Item
                  key={opt.value}
                  id={opt.value}
                  textValue={opt.label}
                >
                  {opt.label}
                </ListBox.Item>
              ))}
            </ListBox>
          </Select.Popover>
        </Select>
      </div>

      {store.isListLoading && store.campaigns.length === 0 ? (
        <div className="pc-loading" data-testid="push-campaigns-loading">
          <Spinner size="lg" />
        </div>
      ) : (
        <Table data-testid="push-campaigns-table">
          <Table.ScrollContainer>
            <Table.Content aria-label="Список рассылок">
              <Table.Header>
                <Table.Column id="name" isRowHeader>
                  Название
                </Table.Column>
                <Table.Column id="status">Статус</Table.Column>
                <Table.Column id="sent">Отправка</Table.Column>
                <Table.Column id="recipients">Получатели</Table.Column>
                <Table.Column id="delivered">
                  <Tooltip delay={300}>
                    <Tooltip.Trigger>
                      <span className="pc-tooltip-trigger">Доставлено</span>
                    </Tooltip.Trigger>
                    <Tooltip.Content>
                      Квитанция доставки APNs/FCM (Expo «ok»), не показ на экране
                    </Tooltip.Content>
                  </Tooltip>
                </Table.Column>
                <Table.Column id="opened">Открыли</Table.Column>
                <Table.Column id="booked">Записались</Table.Column>
                <Table.Column id="paid">Оплатили</Table.Column>
              </Table.Header>
              <Table.Body>
                {store.campaigns.length === 0 ? (
                  <Table.Row>
                    <Table.Cell colSpan={COLUMN_COUNT}>
                      <div className="pc-empty" data-testid="push-campaigns-empty">
                        {store.listError
                          ? "Список недоступен"
                          : "Нет рассылок по этому запросу"}
                      </div>
                    </Table.Cell>
                  </Table.Row>
                ) : (
                  store.campaigns.map((c) => {
                    const sent = c.sentCount ?? c.recipientsTotal ?? 0;
                    const delivered = c.deliveredCount ?? 0;
                    const opened = c.openedCount ?? 0;
                    return (
                      <Table.Row
                        key={c.uuid}
                        id={c.uuid}
                        className="pc-row-link"
                        data-testid={`push-campaign-row-${c.uuid}`}
                        onAction={() =>
                          navigate(
                            ROUTES.PUSH_CAMPAIGN_DETAIL.replace(":uuid", c.uuid),
                          )
                        }
                      >
                        <Table.Cell>{c.name}</Table.Cell>
                        <Table.Cell>
                          {CAMPAIGN_STATUS_LABELS[c.status] ?? c.status}
                        </Table.Cell>
                        <Table.Cell>{formatDateTime(sentAt(c))}</Table.Cell>
                        <Table.Cell>{c.recipientsTotal ?? "—"}</Table.Cell>
                        <Table.Cell>
                          {delivered}
                          {sent > 0 && (
                            <span className="pc-percent">
                              {" "}
                              ({pct(delivered, sent)})
                            </span>
                          )}
                        </Table.Cell>
                        <Table.Cell>
                          {opened}
                          {delivered > 0 && (
                            <span className="pc-percent">
                              {" "}
                              ({pct(opened, delivered)})
                            </span>
                          )}
                        </Table.Cell>
                        <Table.Cell>{c.bookedCount ?? "—"}</Table.Cell>
                        <Table.Cell>{c.paidCount ?? "—"}</Table.Cell>
                      </Table.Row>
                    );
                  })
                )}
              </Table.Body>
            </Table.Content>
          </Table.ScrollContainer>
        </Table>
      )}

      {store.listMeta && store.listMeta.totalPages > 1 && (
        <div className="flex gap-2 justify-end" data-testid="push-campaigns-pagination">
          <Button
            variant="outline"
            isDisabled={!store.listMeta.hasPrevPage}
            data-testid="push-campaigns-pagination-prev"
            onPress={() => store.setListPage(store.listPage - 1)}
          >
            Назад
          </Button>
          <Button
            variant="outline"
            isDisabled={!store.listMeta.hasNextPage}
            data-testid="push-campaigns-pagination-next"
            onPress={() => store.setListPage(store.listPage + 1)}
          >
            Дальше
          </Button>
        </div>
      )}
    </div>
  );
});

PushCampaignsPage.displayName = "PushCampaignsPage";
export default PushCampaignsPage;
